import { differenceInCalendarDays } from 'date-fns';
import { AccommodationPlaceRoomBlockStatus } from '@graphql/generated-base';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { getValidBookingLink } from '@apps/guest/routes/HotelBooking/HotelBooking.utils';

export type MarriottRoomBlockCandidate = Readonly<{
  id: string;
  name?: string | null;
  customName?: string | null;
  accommodationRoomBlock?: Readonly<{
    bookingUrl?: string | null;
    cutoffDate?: string | null;
    status?: AccommodationPlaceRoomBlockStatus | null;
  }> | null;
}>;

export type MarriottRoomBlockBannerTarget = Readonly<{
  placeId: string;
  hotelName: string;
  cutoffDate: Date;
  daysLeft: number;
}>;

/** Only direct Marriott links. Wrapped links (security scanners, redirectors) are skipped on purpose. */
export const isMarriottBookingUrl = (bookingUrl: string | null | undefined): boolean => {
  const hostname = getValidBookingLink(bookingUrl ?? undefined)?.hostname.toLowerCase();
  if (!hostname) {
    return false;
  }
  return hostname === 'marriott.com' || hostname.endsWith('.marriott.com') || hostname === 'marriott.app.link';
};

const isActiveMarriottBlock = (candidate: MarriottRoomBlockCandidate | null | undefined): candidate is MarriottRoomBlockCandidate =>
  candidate?.accommodationRoomBlock?.status === AccommodationPlaceRoomBlockStatus.active && isMarriottBookingUrl(candidate.accommodationRoomBlock.bookingUrl);

/** The banner target for one active Marriott block, or undefined if it has no hotel name or cutoff, or its cutoff has passed. */
const toTarget = (place: MarriottRoomBlockCandidate, now: Date): MarriottRoomBlockBannerTarget | undefined => {
  const block = place.accommodationRoomBlock;
  const hotelName = (place.customName || place.name || '').trim();
  const cutoffDate = block?.cutoffDate ? createDateFromUnformattedString(block.cutoffDate) : null;
  if (!hotelName || !cutoffDate) {
    return undefined;
  }
  const daysLeft = differenceInCalendarDays(cutoffDate, now);
  if (daysLeft < 0) {
    return undefined;
  }
  return { placeId: place.id, hotelName, cutoffDate, daysLeft };
};

/**
 * Decides what the banner advertises. The event qualifies only when its FIRST active room block, in the
 * order the API returns the places, is a direct Marriott link; if any other hotel's block comes first
 * there is no banner. When it qualifies, the banner promotes whichever active Marriott block still open
 * (cutoff today or later) closes soonest, so a guest is pushed toward the nearest deadline. Returns
 * undefined once every Marriott cutoff has passed, which turns the banner off by itself.
 */
export const pickMarriottRoomBlock = (places: ReadonlyArray<MarriottRoomBlockCandidate | null | undefined>, now: Date = new Date()): MarriottRoomBlockBannerTarget | undefined => {
  const firstActive = places.find(candidate => candidate?.accommodationRoomBlock?.status === AccommodationPlaceRoomBlockStatus.active);
  if (!isActiveMarriottBlock(firstActive)) {
    return undefined;
  }
  return places
    .filter(isActiveMarriottBlock)
    .map(place => toTarget(place, now))
    .filter((target): target is MarriottRoomBlockBannerTarget => !!target)
    .reduce<MarriottRoomBlockBannerTarget | undefined>((soonest, target) => (!soonest || target.daysLeft < soonest.daysLeft ? target : soonest), undefined);
};
